// FILE: /src/Components/Public/SharedLinkView.js (FULLY UPDATED)
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Spin, Empty, Button, Avatar, Input, message, Tag, Progress } from 'antd';
import moment from 'moment';
import {
  LockOutlined,
  LikeFilled,
  LikeOutlined,
  MessageOutlined,
  CheckCircleOutlined,
  EyeOutlined
} from '@ant-design/icons';

import FacebookModal from '../Social Desings/FacebookModal';
import InstagarmModal from '../Social Desings/InstagarmModal';
import Pinerest from '../Social Desings/Pinerest';
import LinkedInModal from '../Social Desings/LinkedInModal';
import YoutubeModal from '../Social Desings/YoutubeModal';
import TikTokModal from '../Social Desings/TikTokModal';

const { TextArea } = Input;

/* ---------------------------------------------
   Helpers
--------------------------------------------- */
const getPlatformLabel = (platform) => {
  const map = {
    FACEBOOK: 'Facebook',
    INSTAGRAM: 'Instagram',
    PINTEREST: 'Pinterest',
    LINKEDIN: 'LinkedIn',
    YOUTUBE: 'YouTube',
    TIKTOK: 'TikTok'
  };
  return map[platform] || platform || 'Post';
};

const getStatusLabel = (post) => {
  if (post.status === 'draft') return 'Draft';
  if (post.status === 'scheduled') return 'Scheduled';
  if (post.status === 'failed') return 'Failed';
  return 'Published';
};

const getStatusColor = (status) => {
  switch (status) {
    case 'Draft':
      return 'gold';
    case 'Scheduled':
      return 'blue';
    case 'Failed':
      return 'red';
    case 'Published':
    default:
      return 'green';
  }
};

const getProfileName = (post, profileDetails) => {
  const profileId = post.selectedProfiles?.[0];
  const detail = profileDetails?.[profileId];
  return detail?.profile || 'Profile';
};

const getProfileImage = (post, profileDetails) => {
  const profileId = post.selectedProfiles?.[0];
  const detail = profileDetails?.[profileId];
  return detail?.profile_picture_url || null;
};

/* ---------------------------------------------
   Right-side CTA Panel
--------------------------------------------- */
const CtaPanel = () => (
  <div className="sticky top-8 bg-white p-6 rounded-2xl shadow-lg border border-gray-200 text-center">
    <div className="mb-4">
      <h2 className="text-2xl font-extrabold text-gray-900">Oolook</h2>
      <p className="text-sm text-gray-500 mt-1">Create, review, and publish faster</p>
    </div>

    <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-4 border border-gray-100 mb-6">
      <h3 className="font-semibold text-gray-800 leading-tight">
        Want your own approval links?
      </h3>
      <p className="text-sm text-gray-600 mt-2">
        Share drafts with clients, collect feedback, and manage content in one place.
      </p>
    </div>

    <div className="space-y-3">
      <Link to="/signup">
        <Button type="primary" block size="large" className="font-semibold h-11">
          Sign Up Free
        </Button>
      </Link>
      <Link to="/login">
        <Button block size="large" className="font-semibold h-11">
          Log In
        </Button>
      </Link>
    </div>
  </div>
);

/* ---------------------------------------------
   Password Entry Form
--------------------------------------------- */
const PasswordForm = ({ title, onVerify }) => {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onVerify(password);
    } catch (error) {
      message.error(error.response?.data?.message || 'Incorrect password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex justify-center items-center min-h-screen bg-gradient-to-b from-purple-50 via-blue-50 to-white p-4">
      <div className="max-w-md w-full bg-white p-8 rounded-2xl shadow-xl border border-gray-200 text-center">
        <div className="w-14 h-14 mx-auto rounded-full bg-purple-100 flex items-center justify-center mb-5">
          <LockOutlined className="text-2xl text-purple-600" />
        </div>

        <h2 className="text-2xl font-extrabold text-gray-900">Oolook Review Link</h2>
        <h3 className="text-lg font-semibold text-gray-800 mt-5 mb-2">{title}</h3>
        <p className="text-sm text-gray-500 mb-6">
          This review link is password protected. Enter the password to continue.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Input.Password
            placeholder="Enter password"
            prefix={<LockOutlined />}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            size="large"
          />
          <Button type="primary" htmlType="submit" loading={loading} block size="large">
            Unlock Content
          </Button>
        </form>
      </div>
    </div>
  );
};

/* ---------------------------------------------
   Review Header
--------------------------------------------- */
const ReviewHeader = ({ title, posts }) => {
  const totalPosts = posts?.length || 0;
  const postsWithComments = posts?.filter((p) => (p.comments?.length || 0) > 0).length || 0;
  const totalComments = posts?.reduce((sum, p) => sum + (p.comments?.length || 0), 0) || 0;
  const progressPercent = totalPosts > 0 ? Math.round((postsWithComments / totalPosts) * 100) : 0;

  return (
    <div className="bg-white rounded-2xl shadow-md border border-gray-200 p-6 sm:p-8 mb-8">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Tag color="purple" className="px-3 py-1 rounded-full text-xs font-semibold">
              Review Workspace
            </Tag>
            <Tag color="blue" className="px-3 py-1 rounded-full text-xs font-semibold">
              {totalPosts} Post{totalPosts !== 1 ? 's' : ''}
            </Tag>
          </div>

          <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900">{title}</h1>
          <p className="text-gray-600 mt-2 max-w-2xl">
            Review the content below and leave feedback directly on each post.
          </p>
        </div>

        <div className="text-sm text-gray-500 flex items-center gap-2">
          <CheckCircleOutlined className="text-green-500" />
          Feedback updates in real time
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
          <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Posts Reviewed</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">
            {postsWithComments} / {totalPosts}
          </p>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
          <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Comments</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{totalComments}</p>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
          <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Progress</p>
          <div className="mt-2">
            <Progress percent={progressPercent} showInfo={false} strokeColor="#7c3aed" />
            <p className="text-sm text-gray-700 mt-1 font-medium">{progressPercent}% reviewed</p>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ---------------------------------------------
   Individual Post Card
--------------------------------------------- */
const PublicPostCard = ({ post, shareableId, onInteraction, profileDetails, index }) => {
  const { _id, likes = 0, comments = [] } = post;
  const [isLiked, setIsLiked] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const likedPosts = JSON.parse(localStorage.getItem(`oolook_likes_${shareableId}`) || '[]');
    if (likedPosts.includes(_id)) setIsLiked(true);
  }, [_id, shareableId]);

  const handleLike = async () => {
    if (isLiked) return;
    setIsLiked(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_API}/api/links/public/${shareableId}/posts/${_id}/like`);
      const likedPosts = JSON.parse(localStorage.getItem(`oolook_likes_${shareableId}`) || '[]');
      localStorage.setItem(`oolook_likes_${shareableId}`, JSON.stringify([...likedPosts, _id]));
      onInteraction(_id, 'like');
    } catch (error) {
      if (error.response && error.response.status === 409) {
        console.log("Already liked.");
      } else {
        setIsLiked(false);
        message.error(error.response?.data?.message || 'Could not like post.');
      }
    }
  };

  const handleCommentSubmit = async () => {
    if (!commentText.trim()) return;
    setIsSubmitting(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_API}/api/links/public/${shareableId}/posts/${_id}/comment`,
        { content: commentText }
      );
      const newComment = response.data;
      setCommentText('');
      message.success('Comment added!');
      onInteraction(_id, 'comment', newComment);
    } catch (error) {
      message.error(error.response?.data?.message || 'Could not add comment.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderPreviewComponent = () => {
    const platform = post.platforms?.[0];
    const profileId = post.selectedProfiles?.[0];
    const detail = profileDetails[profileId] || {};

    const profile = {
      platform: detail.platform,
      platform_id: profileId,
      platform_pageId: profileId,
      profile: detail.profile,
      profile_picture_url: detail.profile_picture_url,
    };

    const mediaItems = (post.media_url || []).map(url => ({
      permanentUrl: url,
      type: /\.(mp4|mov|webm)$/i.test(url) ? 'video' : 'image'
    }));

    switch (platform) {
      case 'FACEBOOK':
        return <FacebookModal Previewimages={mediaItems} Previewcaptions={post.message} profile={profile} />;
      case 'INSTAGRAM':
        return (
          <InstagarmModal
            Previewimages={mediaItems}
            Previewcaptions={post.message}
            profile={profile}
            instagramOptions={{ firstComment: post.first_comment }}
          />
        );
      case 'PINTEREST':
        return (
          <Pinerest
            Previewimages={mediaItems}
            pinTitle={post.title}
            pinDescription={post.message}
            profile={profile}
            thumbnailUrl={post.pinterest_thumbnail_url}
          />
        );
      case 'LINKEDIN':
        return (
          <LinkedInModal
            mediaItems={mediaItems}
            captions={post.message}
            profile={profile}
            linkedinOptions={post.platformOptions?.linkedinOptions || {}}
          />
        );
      case 'YOUTUBE':
        return (
          <YoutubeModal
            Previewimages={mediaItems}
            Previewcaptions={post.message}
            profile={profile}
            title={post.platformOptions?.YOUTUBE?.[profileId]?.title || post.title}
          />
        );
      case 'TIKTOK':
        return (
          <TikTokModal
            Previewimages={mediaItems}
            Previewcaptions={post.message}
            profile={profile}
          />
        );
      default:
        return <div className="p-4 text-center text-gray-500">Preview for this platform is not available.</div>;
    }
  };

  const platform = post.platforms?.[0];
  const statusLabel = getStatusLabel(post);
  const profileName = getProfileName(post, profileDetails);
  const profileImage = getProfileImage(post, profileDetails);

  return (
    <div className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden max-w-3xl mx-auto">
      <div className="px-5 sm:px-6 pt-5 pb-4 border-b border-gray-100 bg-gradient-to-r from-white to-gray-50">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <Tag color="purple" className="rounded-full px-3 py-0.5 text-xs font-semibold">
                Post {index + 1}
              </Tag>
              <Tag color="blue" className="rounded-full px-3 py-0.5 text-xs font-semibold">
                {getPlatformLabel(platform)}
              </Tag>
              <Tag color={getStatusColor(statusLabel)} className="rounded-full px-3 py-0.5 text-xs font-semibold">
                {statusLabel}
              </Tag>
            </div>

            <div className="flex items-center gap-3 mt-3">
              <Avatar src={profileImage} className="bg-gray-200" icon={!profileImage ? <EyeOutlined /> : null} />
              <div>
                <p className="text-sm font-semibold text-gray-800">{profileName}</p>
                <p className="text-xs text-gray-500">
                  {post.scheduledTime
                    ? `Scheduled ${moment(post.scheduledTime).format('MMM D, h:mm A')}`
                    : post.date
                    ? `Published ${moment(post.date).format('MMM D, h:mm A')}`
                    : 'Unscheduled'}
                </p>
              </div>
            </div>
          </div>

          <div className="text-xs text-gray-500 font-medium">
            Review & feedback
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-6">
        {renderPreviewComponent()}
      </div>

      <div className="bg-slate-50 border-t p-5 sm:p-6 space-y-5">
        <div>
          <h4 className="font-semibold text-gray-800 text-base">Feedback</h4>
          <p className="text-sm text-gray-500 mt-1">
            Leave approval notes, revision requests, or general comments.
          </p>
        </div>

        <div className="flex items-center gap-6 text-gray-600">
          <button
            onClick={handleLike}
            disabled={isLiked}
            className="flex items-center gap-2 text-base font-semibold hover:text-blue-600 disabled:text-blue-600 disabled:cursor-not-allowed transition-colors"
          >
            {isLiked ? <LikeFilled className="text-xl" /> : <LikeOutlined className="text-xl" />}
            <span>{likes}</span>
          </button>

          <div className="flex items-center gap-2 text-base font-semibold">
            <MessageOutlined className="text-xl" />
            <span>{comments.length}</span>
          </div>
        </div>

        {comments.length > 0 ? (
          <div className="space-y-4 max-h-60 overflow-y-auto pr-2">
            {comments.map((comment) => (
              <div key={comment._id} className="flex items-start gap-3">
                <Avatar size="small" className="bg-gray-200 mt-1">
                  <i className="fa-solid fa-user text-gray-500"></i>
                </Avatar>
                <div className="bg-white border rounded-xl p-3 text-sm flex-grow shadow-sm">
                  <p className="text-gray-800 leading-relaxed">{comment.content}</p>
                  <p className="text-xs text-gray-400 mt-1.5 text-right">
                    {moment(comment.createdAt).fromNow()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white border border-dashed border-gray-300 rounded-xl p-4 text-sm text-gray-500">
            No feedback yet. Add the first comment below.
          </div>
        )}

        <div className="flex items-start gap-3 pt-4 border-t border-gray-200">
          <Avatar>
            <i className="fa-solid fa-user"></i>
          </Avatar>
          <div className="flex-grow">
            <TextArea
              placeholder="Share feedback, revisions, or approval notes..."
              autoSize={{ minRows: 2, maxRows: 4 }}
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
            />
            <div className="flex justify-between items-center mt-2">
              <p className="text-xs text-gray-400">
                Comments are visible to the owner of this review link.
              </p>
              <Button
                type="primary"
                onClick={handleCommentSubmit}
                loading={isSubmitting}
                disabled={!commentText.trim()}
              >
                Send
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ---------------------------------------------
   Main View Component
--------------------------------------------- */
const SharedLinkView = () => {
  const { shareableId } = useParams();
  const [linkData, setLinkData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [requiresPassword, setRequiresPassword] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const fetchLinkData = useCallback(async (isInitialLoad = false) => {
    if (isInitialLoad) setLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_API}/api/links/public/${shareableId}`);
      if (response.data.requiresPassword) {
        setRequiresPassword(true);
        setLinkData(prev => ({ ...prev, title: response.data.title, posts: [], profileDetails: {} }));
      } else {
        setLinkData(response.data);
        if (isInitialLoad) document.title = `${response.data.title} - Oolook Preview`;
      }
    } catch (err) {
      if (isInitialLoad) {
        setError(err.response?.data?.message || 'The link is invalid or has been deleted.');
      } else {
        console.error("Failed to refresh link data, showing stale data.", err);
      }
    } finally {
      if (isInitialLoad) setLoading(false);
    }
  }, [shareableId]);

  const handleVerifyPassword = async (password) => {
    const response = await axios.post(
      `${process.env.REACT_APP_BACKEND_API}/api/links/public/${shareableId}/verify`,
      { password }
    );
    setLinkData(response.data);
    setRequiresPassword(false);
    setIsAuthenticated(true);
    document.title = `${response.data.title} - Oolook Preview`;
  };

  const handleInteraction = useCallback((postId, type, data) => {
    setLinkData(currentData => {
      if (!currentData || !currentData.posts) return currentData;

      const newPosts = currentData.posts.map(post => {
        if (post._id === postId) {
          const updatedPost = { ...post };
          if (type === 'like') updatedPost.likes = (updatedPost.likes || 0) + 1;
          if (type === 'comment' && data) updatedPost.comments = [data, ...(updatedPost.comments || [])];
          return updatedPost;
        }
        return post;
      });

      return { ...currentData, posts: newPosts };
    });
  }, []);

  useEffect(() => {
    document.title = "Oolook - Content Preview";
    fetchLinkData(true);
  }, [shareableId, fetchLinkData]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gradient-to-b from-purple-50 via-blue-50 to-white">
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-screen bg-gradient-to-b from-purple-50 via-blue-50 to-white p-4 text-center">
        <div>
          <h2 className="text-2xl font-extrabold text-gray-900 mb-6">Oolook</h2>
          <Empty description={<span className="text-red-500 font-semibold">{error}</span>}>
            <Button type="primary" href="/login">Go to Oolook Login</Button>
          </Empty>
        </div>
      </div>
    );
  }

  if (requiresPassword && !isAuthenticated) {
    return <PasswordForm title={linkData?.title} onVerify={handleVerifyPassword} />;
  }

  if (!linkData || !linkData.posts) {
    return (
      <div className="flex justify-center items-center h-screen bg-gradient-to-b from-purple-50 via-blue-50 to-white">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 via-blue-50 to-white">
      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-10">
        <ReviewHeader title={linkData.title} posts={linkData.posts} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 xl:gap-12">
          <div className="lg:col-span-2">
            {linkData.posts && linkData.posts.length > 0 ? (
              <div className="space-y-10">
                {linkData.posts.map((post, idx) => (
                  <PublicPostCard
                    key={post._id}
                    post={post}
                    shareableId={shareableId}
                    onInteraction={handleInteraction}
                    profileDetails={linkData.profileDetails || {}}
                    index={idx}
                  />
                ))}
              </div>
            ) : (
              <div className="bg-white p-12 rounded-xl border shadow-sm">
                <Empty description="No posts have been added to this shareable link yet." />
              </div>
            )}
          </div>

          <div className="hidden lg:block">
            <CtaPanel />
          </div>
        </div>
      </main>

      <footer className="text-center py-8 text-sm text-gray-500 mt-10">
        <p>&copy; {new Date().getFullYear()} Oolook. All Rights Reserved.</p>
      </footer>
    </div>
  );
};

export default SharedLinkView;